import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { tryApiRequest, getApiCacheSync, type BackendProduct, type BackendPost, API_BASE } from '../lib/api';
import { DEFAULT_HOME_LAYOUT, type HomeBlock } from '../components/admin/HomeBuilderTab';

const SLUG_IMAGES: Record<string, string> = {
  'bmcu-standard': 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80',
  'bmcu-pro': 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=800&q=80',
  '3d-accessories': 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?auto=format&fit=crop&w=800&q=80',
};

function productImage(slug: string) {
  return SLUG_IMAGES[slug] ?? SLUG_IMAGES['bmcu-standard'];
}

function formatPrice(val: string) {
  if (!val) return 'Liên hệ';
  const num = Number(val);
  if (!isNaN(num)) {
    return num.toLocaleString('vi-VN') + ' VNĐ';
  }
  return val;
}

export default function HomePage() {
  const cachedProducts = getApiCacheSync<{ products: BackendProduct[] }>('/products');
  const cachedPosts = getApiCacheSync<{ posts: any[] }>('/posts');
  
  const [apiProducts, setApiProducts] = useState<BackendProduct[]>(cachedProducts?.products || []);
  const [apiPosts, setApiPosts] = useState<BackendPost[]>(cachedPosts?.posts || []);
  
  // First try memory cache from api.ts
  let initialBlocks: HomeBlock[] = [];
  let foundInCache = false;
  if (cachedPosts?.posts) {
    const layoutPost = cachedPosts.posts.find(p => p.slug === 'home-layout');
    if (layoutPost) {
      try {
        initialBlocks = JSON.parse(layoutPost.content);
        foundInCache = true;
      } catch {}
    }
  }

  // If memory cache is empty (e.g. fresh reload), try localStorage
  if (!foundInCache) {
    try {
      const local = localStorage.getItem('home_layout_cache');
      if (local) {
        initialBlocks = JSON.parse(local);
      }
    } catch {}
  }

  if (!initialBlocks || initialBlocks.length === 0) {
    initialBlocks = DEFAULT_HOME_LAYOUT;
  }

  const [blocks, setBlocks] = useState<HomeBlock[]>(initialBlocks);
  const [isReady, setIsReady] = useState(!!(cachedProducts && cachedPosts));


  useEffect(() => {
    async function load() {
      try {
        const [productsRes, postsRes] = await Promise.all([
          tryApiRequest<{ products: BackendProduct[] }>('/products', { useCache: true }),
          tryApiRequest<{ posts: any[] }>('/posts', { useCache: true })
        ]);

      if (productsRes?.products?.length) {
        setApiProducts(productsRes.products.filter(p => p.isActive));
      }

      if (postsRes?.posts) {
        const layoutPost = postsRes.posts.find(p => p.slug === 'home-layout');
        if (layoutPost) {
          try {
            const newBlocks = JSON.parse(layoutPost.content);
            setBlocks(prev => {
              if (JSON.stringify(prev) !== JSON.stringify(newBlocks)) return newBlocks;
              return prev;
            });
            localStorage.setItem('home_layout_cache', layoutPost.content);
          } catch (e) {
            console.error('Invalid home layout JSON');
          }
        }
        const validPosts = postsRes.posts
          .filter(p => p.category !== 'System' && p.category !== 'Trang' && !['about-page', 'site-meta', 'site-footer', 'home-layout'].includes(p.slug) && !p.slug.startsWith('site-') && !p.slug.startsWith('home-'))
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setApiPosts(validPosts);
      }
      } finally {
        setIsReady(true);
      }
    }
    load();
  }, []);

  const renderBlock = (block: HomeBlock) => {
    switch (block.type) {
      case 'Hero':
        return (
          <section key={block.id} className="grid-bg" style={{ position: 'relative', overflow: 'hidden', padding: '120px 24px 90px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', borderBottom: '1px solid var(--glass-border)' }}>
            <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', width: '60vw', height: '60vw', background: 'radial-gradient(circle, rgba(239, 68, 68, 0.15) 0%, transparent 60%)', filter: 'blur(60px)', zIndex: 0, pointerEvents: 'none' }} />
            
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '1440px', margin: '0 auto', width: '100%' }}>
              <motion.div 
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.7, ease: "easeOut" }}
                style={{ textAlign: 'center', maxWidth: '960px', margin: '0 auto' }}
              >
                <motion.span initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: false, amount: 0.2 }} transition={{ delay: 0.1, duration: 0.5 }} className="eyebrow" style={{ marginBottom: '24px' }}>
                  {block.eyebrow}
                </motion.span>
                <motion.h1 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ delay: 0.2, duration: 0.6 }} style={{ fontSize: 'clamp(2.8rem, 5.5vw, 4.5rem)', fontWeight: 800, margin: '0 0 24px', lineHeight: 1.15, color: '#ffffff', letterSpacing: '-0.02em' }}>
                  {block.title}
                </motion.h1>
                <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: false, amount: 0.2 }} transition={{ delay: 0.3, duration: 0.6 }} style={{ fontSize: '1.25rem', color: '#94a3b8', lineHeight: 1.65, marginBottom: '40px', whiteSpace: 'pre-wrap' }}>
                  {block.description}
                </motion.p>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ delay: 0.4, duration: 0.5 }} className="hero-actions" style={{ justifyContent: 'center', marginTop: 0 }}>
                  {block.primaryButtonText && (
                    <Link className="button primary glow-border" style={{ padding: '1rem 2.5rem', fontSize: '1.1rem', borderRadius: '12px' }} to={block.primaryButtonLink}>
                      {block.primaryButtonText}
                    </Link>
                  )}
                  {block.secondaryButtonText && (
                    <Link className="button secondary glow-border" style={{ padding: '1rem 2.5rem', fontSize: '1.1rem', borderRadius: '12px' }} to={block.secondaryButtonLink}>
                      {block.secondaryButtonText}
                    </Link>
                  )}
                </motion.div>
              </motion.div>
            </div>
          </section>
        );

      case 'Stats':
        return (
          <section key={block.id} className="section" style={{ padding: '60px 24px', background: 'rgba(255,255,255,0.02)', borderTop: '1px solid var(--glass-border)', borderBottom: '1px solid var(--glass-border)' }}>
            <div style={{ maxWidth: '1440px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '40px' }}>
              {block.items.map((item, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.6, delay: idx * 0.1, ease: 'easeOut' }}
                  style={{ textAlign: 'center', padding: '20px 40px', minWidth: '200px' }}
                >
                  <div style={{ fontSize: '3.2rem', fontWeight: 900, color: 'var(--neon-red)', marginBottom: '8px', lineHeight: 1 }}>{item.value}</div>
                  <div style={{ fontSize: '1.1rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>{item.label}</div>
                </motion.div>
              ))}
            </div>
          </section>
        );

      case 'SplitBanner':
        const isImageLeft = block.imagePosition === 'left';
        return (
          <section key={block.id} style={{ margin: '48px auto', maxWidth: '1440px', padding: '0 24px', width: '100%' }}>
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.7, ease: 'easeOut' }} className="split-banner-container" style={{
              display: 'flex',
              flexDirection: isImageLeft ? 'row-reverse' : 'row',
              flexWrap: 'wrap',
              overflow: 'hidden',
              alignItems: 'stretch',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              background: 'linear-gradient(135deg, rgba(24, 28, 38, 0.85) 0%, rgba(14, 16, 22, 0.95) 100%)',
              boxShadow: '0 20px 45px -10px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
            }}>
              <div className="split-banner-content" style={{ flex: '1 1 420px', padding: '60px 48px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {block.eyebrow && <span className="eyebrow" style={{ display: 'inline-block', marginBottom: '16px', alignSelf: 'flex-start' }}>{block.eyebrow}</span>}
                <h2 style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)', marginBottom: '20px', color: '#f8fafc', lineHeight: 1.2, fontWeight: 800, letterSpacing: '-0.02em' }}>{block.title}</h2>
                <p style={{ fontSize: '1.1rem', color: '#94a3b8', marginBottom: '32px', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{block.description}</p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {block.primaryButtonText && (
                    <Link className="button primary glow-border" style={{ padding: '0.85rem 2.2rem', fontSize: '1.05rem', borderRadius: '12px' }} to={block.primaryButtonLink}>
                      {block.primaryButtonText}
                    </Link>
                  )}
                  {block.secondaryButtonText && (
                    <Link className="button secondary glow-border" style={{ padding: '0.85rem 2.2rem', fontSize: '1.05rem', borderRadius: '12px' }} to={block.secondaryButtonLink}>
                      {block.secondaryButtonText}
                    </Link>
                  )}
                </div>
              </div>
              <div className="split-banner-image" style={{
                flex: '1 1 480px',
                backgroundImage: `url(${block.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '420px',
                position: 'relative'
              }}>
                <div style={{ position: 'absolute', inset: 0, background: isImageLeft ? 'linear-gradient(to left, rgba(14,16,22,0.4) 0%, transparent 50%)' : 'linear-gradient(to right, rgba(14,16,22,0.4) 0%, transparent 50%)' }} />
              </div>
            </motion.div>
          </section>
        );

      case 'MiniCards':
        return (
          <section key={block.id} style={{ maxWidth: '1440px', margin: '36px auto', padding: '0 24px', width: '100%' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
              {block.cards.map((card, idx) => {
                const accentColor = (!card.labelColor || card.labelColor === '#000000' || card.labelColor === '#0B0C10') ? '#ef4444' : card.labelColor;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: idx * 0.1, ease: 'easeOut' }}
                    className="glass-card"
                    style={{ 
                      position: 'relative',
                      overflow: 'hidden',
                      textAlign: 'center', 
                      padding: '36px 28px',
                      borderRadius: '20px',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      background: 'linear-gradient(180deg, rgba(26, 31, 44, 0.8) 0%, rgba(15, 18, 26, 0.9) 100%)',
                      boxShadow: '0 12px 32px -4px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(16px)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-6px)';
                      e.currentTarget.style.borderColor = accentColor;
                      e.currentTarget.style.boxShadow = `0 20px 40px -8px rgba(0,0,0,0.6), 0 0 20px ${accentColor}33`;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.boxShadow = '0 12px 32px -4px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.08)';
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '15%',
                      right: '15%',
                      height: '3px',
                      background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
                      boxShadow: `0 0 12px ${accentColor}`,
                      borderRadius: '0 0 4px 4px'
                    }} />
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 14px',
                      borderRadius: '999px',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      letterSpacing: '1.2px',
                      background: `${accentColor}18`,
                      color: accentColor,
                      border: `1px solid ${accentColor}33`,
                      marginBottom: '16px',
                      textTransform: 'uppercase'
                    }}>
                      {card.label}
                    </span>
                    <strong style={{ fontSize: '1.35rem', fontWeight: 700, marginBottom: '12px', color: '#f8fafc', display: 'block', lineHeight: 1.3 }}>
                      {card.title}
                    </strong>
                    <span style={{ color: '#94a3b8', fontSize: '1.02rem', lineHeight: '1.6', display: 'block' }}>
                      {card.description}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </section>
        );

      case 'ProductsList':
        const visibleProducts = apiProducts.slice(0, block.limit);
        return (
          <section key={block.id} className="section" style={{ maxWidth: '1440px', margin: '40px auto', padding: '0 24px', width: '100%' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.5 }} className="section-heading center">
              {block.eyebrow && <span className="eyebrow">{block.eyebrow}</span>}
              <h2 style={{ color: '#f8fafc' }}>{block.title}</h2>
            </motion.div>
            <div className="product-grid">
              {visibleProducts.length > 0 ? visibleProducts.map((product, idx) => {
                let coverImg = productImage(product.slug);
                try {
                  const imgs = JSON.parse(product.images || '[]');
                  if (imgs.length > 0) coverImg = API_BASE.replace('/api', '') + imgs[0];
                } catch {}
                
                return (
                  <motion.article initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.5, delay: idx * 0.08, ease: 'easeOut' }} className="product-card glass-card" key={product.id}>
                    <Link to={`/products/${product.slug}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block', overflow: 'hidden' }}>
                      <div className="product-image" style={{ backgroundImage: `url("${coverImg}")`, transition: 'transform 0.5s ease' }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />
                    </Link>
                    <div className="product-body" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <Link to={`/products/${product.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <h3 style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          margin: '0 0 12px 0',
                          lineHeight: '1.4',
                          color: '#f1f5f9'
                        }}>
                          {product.name}
                        </h3>
                      </Link>
                      <div className="inline-info" style={{ marginTop: 'auto', marginBottom: '16px' }}>
                        <strong style={{ fontSize: '1.25rem', color: 'var(--neon-red)' }}>{formatPrice(product.priceDisplay)}</strong>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Link 
                          className="button primary small glow-border" 
                          style={{ flex: 1, textAlign: 'center', fontWeight: 'bold' }} 
                          to={product.ecommerceLink && product.ecommerceLink !== 'https://' ? product.ecommerceLink : `/products/${product.slug}`} 
                          target={product.ecommerceLink && product.ecommerceLink !== 'https://' ? "_blank" : undefined}
                        >
                          {product.buttonText || 'Mua Ngay'}
                        </Link>
                      </div>
                    </div>
                  </motion.article>
                );
              }) : !isReady ? (
                Array.from({ length: Math.min(block.limit, 4) }).map((_, i) => (
                  <div key={i} className="glass-card" style={{ height: '380px', opacity: 0.6 }} />
                ))
              ) : (
                <div style={{ minHeight: '200px', gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Chưa có sản phẩm nào.</div>
              )}
            </div>
          </section>
        );

      case 'Features':
        return (
          <section key={block.id} className="section alt" style={{ maxWidth: '1440px', margin: '40px auto', padding: '0 24px', width: '100%' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.5 }} className="section-heading center">
              {block.eyebrow && <span className="eyebrow">{block.eyebrow}</span>}
              <h2 style={{ color: '#f8fafc' }}>{block.title}</h2>
            </motion.div>
            <div className="feature-grid">
              {block.items.map((item, idx) => (
                <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.5, delay: idx * 0.1, ease: 'easeOut' }} key={idx} className="feature-card glass-card">
                  <div className="feature-icon" style={{ background: 'var(--glass-border)', color: 'var(--neon-red)' }}>{item.icon}</div>
                  <h3 style={{ color: '#f8fafc' }}>{item.title}</h3>
                  <p style={{ color: '#94a3b8' }}>{item.description}</p>
                </motion.div>
              ))}
            </div>
          </section>
        );

      case 'LatestNews':
        const visiblePosts = apiPosts.slice(0, block.limit);
        return (
          <section key={block.id} className="section" style={{ maxWidth: '1440px', margin: '40px auto', padding: '0 24px', width: '100%' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.5 }} className="section-heading center" style={{ marginBottom: '40px' }}>
              {block.eyebrow && <span className="eyebrow" style={{ display: 'inline-block', marginBottom: '16px' }}>{block.eyebrow}</span>}
              <h2 style={{ fontSize: '2.5rem', color: '#f8fafc' }}>{block.title}</h2>
            </motion.div>
            {visiblePosts.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '32px' }}>
                {visiblePosts.map((post, idx) => {
                  return (
                    <motion.div key={post.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.5, delay: idx * 0.08, ease: 'easeOut' }}>
                      <Link className="glass-card" to={`/news/${post.slug}`} style={{ display: 'block', textDecoration: 'none', overflow: 'hidden', height: '100%' }}>
                        {post.coverImage && (
                          <div style={{ height: '200px', width: '100%', overflow: 'hidden' }}>
                            <img src={post.coverImage} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                        )}
                        <div style={{ padding: '24px' }}>
                          <div style={{ fontSize: '0.85rem', color: 'var(--neon-red)', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>{post.category}</div>
                          <h3 style={{ fontSize: '1.25rem', color: '#f8fafc', marginBottom: '12px', lineHeight: 1.4 }}>{post.title}</h3>
                          {post.excerpt && <p style={{ fontSize: '0.95rem', color: '#94a3b8', lineHeight: 1.6, marginBottom: '16px', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.excerpt}</p>}
                          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{post.createdAt ? new Date(post.createdAt).toLocaleDateString('vi-VN') : ''}</div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            ) : !isReady ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '32px' }}>
                {Array.from({ length: Math.min(block.limit, 3) }).map((_, i) => (
                  <div key={i} className="glass-card" style={{ height: '360px', opacity: 0.6 }} />
                ))}
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#94a3b8' }}>Chưa có bài viết nào.</p>
            )}
          </section>
        );

      case 'CallToAction':
        return (
          <section key={block.id} className="section" style={{ margin: '60px auto', maxWidth: '1440px', padding: '0 24px', width: '100%' }}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 40 }} whileInView={{ opacity: 1, scale: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.7, ease: 'easeOut' }} style={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, #0f172a 0%, #0B0C10 100%)',
              border: '1px solid var(--glass-border)',
              color: 'white',
              borderRadius: '24px',
              padding: '80px 20px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)'
            }}>
              <div style={{ position: 'absolute', top: '-50%', left: '-10%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(239, 68, 68, 0.15) 0%, transparent 70%)', borderRadius: '50%' }}></div>
              <div style={{ position: 'absolute', bottom: '-50%', right: '-10%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(239,68,68,0.1) 0%, transparent 70%)', borderRadius: '50%' }}></div>
              <div style={{ position: 'relative', zIndex: 1, maxWidth: '600px', margin: '0 auto' }}>
                <h2 style={{ color: 'white', fontSize: '2.5rem', marginBottom: '16px', fontWeight: 800, lineHeight: 1.2 }}>{block.title}</h2>
                <Link to={block.buttonLink} className="button primary glow-border" style={{ fontSize: '1.1rem', padding: '1rem 2.5rem', borderRadius: '12px' }}>{block.buttonText}</Link>
              </div>
            </motion.div>
          </section>
        );

      default:
        return null;
    }
  };

  // Remove full page block so Hero banner renders instantly


  return (
    <>
      {blocks.map(renderBlock)}
    </>
  );
}
